import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { isNative } from "@/lib/platform";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PropertyProvider } from "@/contexts/PropertyContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import HomeRecords from "./pages/HomeRecords";
import Profile from "./pages/Profile";
import Directory from "./pages/Directory";
import ServiceProviders from "./pages/ServiceProviders";
import ProductDetails from "./pages/ProductDetails";
import MaintenanceDetail from "./pages/MaintenanceDetail";
import Auth from "./pages/Auth";
import AdminPortal from "./pages/AdminPortal";
import ClaimProperty from "./pages/ClaimProperty";
import SetupAccount from "./pages/SetupAccount";
import BottomNav from "./components/BottomNav";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import MarketingSite from "./pages/MarketingSite";
import Features from "./pages/Features";
import CookiePolicy from "./pages/CookiePolicy";

import LoadingScreen from "@/components/LoadingScreen";
import AskHomeEzi from "@/components/AskHomeEzi";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider } from "@/components/ThemeProvider";

import { useNotifications } from "@/hooks/useNotifications";
import CookieBanner from "@/components/CookieBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import OfflineBanner from "@/components/OfflineBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Prevents background refetches and reloading indicators when returning to the tab
      staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes to minimize redundant requests
    },
  },
});

const NotificationsWrapper = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <NotificationsContent />;
};

const NotificationsContent = () => {
  useNotifications();
  return null;
};

const App = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider defaultTheme="light" storageKey="homeezi-theme">
                <AuthProvider>
                    <PropertyProvider>
                        <TooltipProvider>
                            <AppContent />
                        </TooltipProvider>
                    </PropertyProvider>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
};

const GlobalAuthRedirects = () => {
    const { isPasswordRecovery } = useAuth();
    const navigate = useNavigate();

    // If we detect a password recovery event, force the user to the auth page
    useEffect(() => {
        if (isPasswordRecovery && window.location.pathname !== "/auth") {
            navigate("/auth", { replace: true });
        }
    }, [isPasswordRecovery, navigate]);
    return null;
};

const RouteChangeListener = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        // Reset body pointer-events and overflow that might get stuck due to Radix UI dialogs/sheets being unmounted during navigation
        document.body.style.pointerEvents = 'auto';
        document.body.style.overflow = 'unset';
        document.body.classList.remove('popup-open');
    }, [pathname]);

    return null;
};

const AppContent = () => {
    const { loading } = useAuth();

    useEffect(() => {
        if (!loading) {
            import("@capacitor/splash-screen")
                .then(({ SplashScreen }) => {
                    SplashScreen.hide().catch((err) => {
                        console.warn("Failed to hide splash screen:", err);
                    });
                })
                .catch((err) => {
                    console.warn("Capacitor SplashScreen plugin not available:", err);
                });
        }
    }, [loading]);

    useEffect(() => {
        if (typeof window === "undefined" || !Capacitor.isPluginAvailable("Keyboard")) return;

        let savedScrollY = 0;

        const showListener = Keyboard.addListener("keyboardWillShow", () => {
            savedScrollY = window.scrollY;
        });

        const didShowListener = Keyboard.addListener("keyboardDidShow", () => {
            const activeElement = document.activeElement;
            if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
                setTimeout(() => {
                    activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
        });

        const hideListener = Keyboard.addListener("keyboardWillHide", () => {
            setTimeout(() => {
                window.scrollTo(0, savedScrollY);
            }, 100);
        });

        return () => {
            showListener.remove();
            didShowListener.remove();
            hideListener.remove();
        };
    }, []);

    return (
        <BrowserRouter>
            <OfflineBanner />
            <GlobalAuthRedirects />
            <RouteChangeListener />
            {/* Loading screen overlays everything while auth initializes */}
            <AnimatePresence>
                {loading && <LoadingScreen key="loading" />}
            </AnimatePresence>

            <NotificationsWrapper />
            <Toaster />
            <Sonner />
            <ErrorBoundary>
                <Routes>
                    <Route path="/auth" element={<Auth />} />
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <Index />
                        </ProtectedRoute>
                    }
                />
                {/* /app kept as redirect for backward compat (native builds, bookmarks) */}
                <Route path="/app" element={<Navigate to="/" replace />} />
                <Route
                    path="/site"
                    element={isNative() ? <Navigate to="/" replace /> : <MarketingSite />}
                />
                <Route
                    path="/home-records"
                    element={
                        <ProtectedRoute>
                            <HomeRecords />
                        </ProtectedRoute>
                    }
                />
                {/* Notification Deep Link Compatibility Routes */}
                <Route path="/plants" element={<ProtectedRoute><Navigate to="/home-records?popup=plants" replace /></ProtectedRoute>} />
                <Route path="/vault" element={<ProtectedRoute><Navigate to="/home-records?popup=appliances" replace /></ProtectedRoute>} />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <Profile />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/directory"
                    element={
                        <ProtectedRoute>
                            <Directory />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/service-providers"
                    element={
                        <ProtectedRoute>
                            <ServiceProviders />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/product-details"
                    element={
                        <ProtectedRoute>
                            <ProductDetails />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/maintenance-detail"
                    element={
                        <ProtectedRoute>
                            <MaintenanceDetail />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin-portal"
                    element={
                        <ProtectedRoute>
                            <AdminPortal />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/claim-property"
                    element={
                        <ProtectedRoute>
                            <ClaimProperty />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/setup"
                    element={
                        <ProtectedRoute>
                            <SetupAccount />
                        </ProtectedRoute>
                    }
                />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/features" element={<Features />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            </ErrorBoundary>
            <BottomNav />
            <CookieBanner />
        </BrowserRouter>
    );
};

export default App;
